"use client";

import { useEffect } from "react";
import config from "@/app/context/config";
import { useAppSelector } from "@/app/redux/hooks";

/**
 * Applies the customizer state to <html> so the CSS in css/theme/colors.css can
 * react to it: the `dark` class drives both the dark palette and Tailwind's
 * `dark:` variant.
 *
 * There is no `data-theme` any more. The app has one palette, the picker that
 * chose between eight is gone, and an attribute nothing selects on is a promise
 * that the choice still exists.
 *
 * This reads customizerSlice directly: it does not depend on MUI, and it stays
 * as-is once MUI is gone.
 *
 * `dir` is not written any more. RTL was cosmetic: it worked through
 * stylis-plugin-rtl flipping emotion's MUI styles, and nothing flips the
 * Tailwind classes this app uses, so the setting flipped an attribute and
 * nothing else. Real RTL means moving the physical utilities (ml-/mr-/left-/
 * right-) to logical ones (ms-/me-/start-/end-), not a toggle.
 */
export default function ThemeAttributes() {
	const { activeMode, activeLayout, isCardShadow, isCollapse } = useAppSelector(
		(state) => state.customizer,
	);

	useEffect(() => {
		const root = document.documentElement;
		root.classList.toggle("dark", activeMode === "dark");

		/*
			The browser chrome follows the app, not the OS.
			
			`viewport.themeColor` in layout.tsx can only answer
			`prefers-color-scheme`, and dark mode here is a toggle in the app that is
			allowed to disagree with it: a user on a light phone running the app dark
			got a white address bar above a near-black page. The value is read off
			the surface token so it cannot drift from the palette.
		*/
		const surface = getComputedStyle(root)
			.getPropertyValue("--brand-surface")
			.trim();

		if (surface) {
			let meta = document.querySelector<HTMLMetaElement>(
				'meta[name="theme-color"]:not([media])',
			);

			if (!meta) {
				meta = document.createElement("meta");
				meta.name = "theme-color";
				document.head.appendChild(meta);
			}

			meta.content = surface;
		}
	}, [activeMode]);

	/*
	 * Card style was a property of the MUI theme object (the MuiCard override in
	 * Components.tsx), so it stopped doing anything when that theme was deleted.
	 * It is an attribute now: data-card-style swaps the shadow/border pair in
	 * globals.css. Radius is no longer here - it is shadcn's own `--radius`,
	 * fixed, not a setting.
	 */
	useEffect(() => {
		const root = document.documentElement;
		root.setAttribute("data-card-style", isCardShadow ? "shadow" : "border");
	}, [isCardShadow]);

	/*
	 * Publishes how much horizontal room the sidebar takes, so overlay UI can
	 * size and centre itself against the content area instead of the viewport.
	 * Modals are `position: fixed`, so they know nothing about the sidebar on
	 * their own and would otherwise sit half underneath it.
	 *
	 * The vertical sidebar is a fixed 200px and only exists at >= 1200px: the
	 * same breakpoint Sidebar.tsx uses to switch between the permanent rail and
	 * the mobile sheet. Horizontal layout has no sidebar at all.
	 */
	useEffect(() => {
		const root = document.documentElement;
		const mql = window.matchMedia("(min-width: 1200px)");

		const apply = () => {
			const hasSidebar = activeLayout !== "horizontal" && mql.matches;
			// The mini rail's hover expansion floats over the page, so the inset
			// tracks the collapsed width and does not jump as the cursor moves.
			const width =
				isCollapse === "mini-sidebar"
					? config.miniSidebarWidth
					: config.sidebarWidth;
			root.style.setProperty(
				"--app-sidebar-inset",
				`${hasSidebar ? width : 0}px`,
			);
		};

		apply();
		mql.addEventListener("change", apply);
		return () => mql.removeEventListener("change", apply);
	}, [activeLayout, isCollapse]);

	return null;
}
